<template>
  <ReportTable
    name="Injests"
    item-key="id"
    :headers="filteredHeaders"
    :filter-fields="filters"
    :provider="ingestAPI.assets360.get360Assets"
    :copy-to-clipboard="false"
    :hide-show="false"
    :show-bookmarks="false"
  >
    <template #actions-left>
      <ERow justify="start" align="center">
        <v-btn icon color="primary" @click="refreshTable()"
          ><v-icon>fa-rotate-right</v-icon></v-btn
        >
        <div class="body-1 text--secondary my-3">
          Queued 360s ({{ queue360Size }}) | Total Queued ({{ totalQueueSize }})
        </div>
      </ERow>
    </template>
    <template #actions-right>
      <ERow class="mr-1">
        <v-spacer />
        <v-btn
          v-if="allowUpload"
          small
          color="primary"
          class="text-center ml-2"
          @click="assets360Dialog = true"
        >
          New {{ assets360UploadType }}
        </v-btn>
        <CreateUploadDialog
          :upload-dialog="assets360Dialog"
          :ingest-upload-type="assets360UploadType"
          :provider="generate360UploadLink"
          :upload-object="uploadObject"
          @close-dialog="on360UploadDialogClosed"
        >
          <template v-if="!uploadObject" #form-append>
            <ValidationProvider
              v-slot="{ errors }"
              name="Date"
              rules="required"
            >
              <v-menu
                v-model="datePickerMenu"
                :close-on-content-click="false"
                :nudge-top="30"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="currentSelectedDate"
                    label="Date"
                    class="mt-5"
                    readonly
                    :error-messages="errors"
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="currentSelectedDate"
                  light
                  @input="datePickerMenu = false"
                />
              </v-menu>
            </ValidationProvider>
            <ValidationProvider
              v-slot="{ errors }"
              name="Floor"
              rules="required"
            >
              <v-text-field
                v-model="currentSelectedFloor"
                label="Floor"
                outlined
                dense
                :error-messages="errors"
              />
            </ValidationProvider>
          </template>
        </CreateUploadDialog>
      </ERow>
    </template>
    <template #item.projectId="{ item }">
      <span
        >{{ item.projectId }}
        <OpenInNewWindowButton
          v-if="isItemValid(item)"
          :target-link="buildQuery(item, !isAdmin)"
        />
      </span>
    </template>
    <template #item.floor="{ item }">
      <span
        class="d-flex align-center w-full"
        style="justify-content: space-between"
      >
        <a
          v-if="isItemValid(item)"
          :href="buildQuery(item, isAdmin)"
          target="_blank"
        >
          <span class="mr-2">{{ item.floor }}</span>
        </a>
        <span v-else class="mr-2">{{ item.floor }}</span>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              :style="{ minWidth: 'unset' }"
              color="primary"
              class="pr-1 ma-0"
              small
              text
              v-bind="attrs"
              v-on="on"
              @click="copyFloor(item.floor)"
            >
              <v-icon x-small class="mr-2">far fa-copy</v-icon>
            </v-btn>
          </template>
          Copy Floor
        </v-tooltip>
      </span>
    </template>
    <template #item.videos="{ item }">
      <ERow justify="center" align="center">
        <v-btn
          v-if="!item.videos || item.videos.length <= 0"
          :disabled="!allowUpload"
          icon
          color="primary"
          target="_blank"
          @click="openUploadDialog(item, assetTypeOf360.Video)"
        >
          <v-icon color="primary"> fa fa-external-link-alt </v-icon>
        </v-btn>
        <ArchiveDownloadLabel
          v-else
          :dataId="item.id"
          :dataType="assetTypeOf360.Video"
          :download-archive="ingestAPI.assets360.getArchive"
        />
      </ERow>
    </template>
    <template #item.images="{ item }">
      <ERow justify="center" align="center">
        <v-btn
          v-if="!item.images || item.images.length <= 0"
          :disabled="!allowUpload"
          icon
          color="primary"
          target="_blank"
          @click="openUploadDialog(item, assetTypeOf360.Image)"
        >
          <v-icon color="primary"> fa fa-external-link-alt </v-icon>
        </v-btn>
        <ArchiveDownloadLabel
          v-else
          :dataId="item.id"
          :dataType="assetTypeOf360.Image"
          :download-archive="ingestAPI.assets360.getArchive"
        />
      </ERow>
    </template>
    <template #item.model="{ item }">
      <ERow justify="center" align="center">
        <v-btn
          v-if="!item.model"
          :disabled="!allowUpload"
          icon
          color="primary"
          target="_blank"
          @click="openUploadDialog(item, assetTypeOf360.Model)"
        >
          <v-icon color="primary"> fa fa-external-link-alt </v-icon>
        </v-btn>
        <ArchiveDownloadLabel
          v-else
          :dataId="item.id"
          :dataType="assetTypeOf360.Model"
          :download-archive="ingestAPI.assets360.getArchive"
        />
      </ERow>
    </template>
    <template #item.route="{ item }">
      <ERow justify="center" align="center">
        <v-btn
          v-if="!item.route"
          :disabled="!allowUpload"
          icon
          color="primary"
          target="_blank"
          @click="openUploadDialog(item, assetTypeOf360.Route)"
        >
          <v-icon color="primary"> fa fa-external-link-alt </v-icon>
        </v-btn>
        <ArchiveDownloadLabel
          v-if="item.route"
          :dataId="item.id"
          :dataType="assetTypeOf360.Route"
          :download-archive="ingestAPI.assets360.getArchive"
        />
      </ERow>
    </template>
    <template #item.mobilePhotos="{ item }">
      <ERow justify="center" align="center">
        <v-btn
          v-if="!item.mobilePhotos || item.mobilePhotos.length === 0"
          :disabled="!allowUpload"
          icon
          color="primary"
          target="_blank"
          @click="openUploadDialog(item, assetTypeOf360.MobilePhoto)"
        >
          <v-icon color="primary"> fa fa-external-link-alt </v-icon>
        </v-btn>
        <ArchiveDownloadLabel
          v-else
          :dataId="item.id"
          :dataType="assetTypeOf360.MobilePhoto"
          :download-archive="ingestAPI.assets360.getArchive"
        />
      </ERow>
    </template>
    <template #item.mobileAudios="{ item }">
      <ERow justify="center" align="center">
        <v-btn
          v-if="!item.mobileAudios || item.mobileAudios.length === 0"
          :disabled="!allowUpload"
          icon
          color="primary"
          target="_blank"
          @click="openUploadDialog(item, assetTypeOf360.MobileAudio)"
        >
          <v-icon color="primary"> fa fa-external-link-alt </v-icon>
        </v-btn>
        <ArchiveDownloadLabel
          v-else
          :dataId="item.id"
          :dataType="assetTypeOf360.MobileAudio"
          :download-archive="ingestAPI.assets360.getArchive"
        />
      </ERow>
    </template>
    <template #item.delete="{ item }">
      <ERow justify="center" align="center">
        <v-btn
          icon
          color="primary"
          target="_blank"
          @click="openDeleteDialog(item)"
        >
          <v-icon color="red"> fas fa-trash </v-icon>
        </v-btn>
      </ERow>
    </template>
  </ReportTable>
</template>

<script>
import CreateUploadDialog from "@/components/ingest/uploadDialogs/CreateUploadDialog"
import ArchiveDownloadLabel from "@/components/ingest/ArchiveDownloadLabel"
import OpenInNewWindowButton from "@/components/OpenInNewWindowButton"
import ReportTable from "@/components/ReportTable"
import { HEADERS_360 } from "@/components/ingest/ingestHeaders"
import { FILTERS_ASSETS_360 } from "@/components/ingest/ingestFilters"
import { _360AssetType } from "@evercam/shared/types"
import { INGEST_UPLOADS_TYPES } from "@evercam/shared/constants/ingest"
import { IngestApi } from "@evercam/shared/api/ingestApi"
import { mapStores } from "pinia"
import { useAccountStore } from "@/stores/account"
import axios from "axios"

export default {
  components: {
    CreateUploadDialog,
    ReportTable,
    ArchiveDownloadLabel,
    OpenInNewWindowButton,
  },
  props: {
    allowUpload: {
      type: Boolean,
      default: true,
    },
    allowDelete: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    headers: HEADERS_360,
    filters: FILTERS_ASSETS_360,
    assets360Dialog: false,
    assets360UploadType: INGEST_UPLOADS_TYPES._360Assets,
    assetTypeOf360: _360AssetType,
    uploadObject: undefined,
    datePickerMenu: false,
    currentSelectedDate: "",
    currentSelectedFloor: "",
    queue360Size: "...",
    totalQueueSize: "...",
    projects: [],
    projectIds: [],
    isAdmin: true,
  }),
  computed: {
    ...mapStores(useAccountStore),
    ingestAPI() {
      return IngestApi
    },
    filteredHeaders() {
      return this.headers.filter((header) => {
        if (header.value === "delete") {
          return this.allowDelete
        }

        return true
      })
    },
  },
  async mounted() {
    await this.fetchProjectData()
    await this.refreshTable()
  },
  methods: {
    async fetchProjectData() {
      try {
        const { data } = await axios.get(
          `${this.$config.public.firebaseProdLink}/data/360-list.json`
        )
        const projectIdList = data ? Object.values(data) : []
        this.projectIds = projectIdList

        // Fetch all projects in parallel
        const projectFetches = await Promise.all(
          projectIdList.map(async (p) => {
            const res = await axios.get(
              `${this.$config.public.firebaseProdLink}/data/projects/360/${p.projectId}/dates.json`
            )
            const dates = res.data ? Object.values(res.data) : []

            return {
              projectId: p.projectId,
              dates,
            }
          })
        )

        this.projects = projectFetches
      } catch (error) {
        this.$notifications.error({
          text: "Couldn't fetch project IDs or projects",
          error,
        })
        this.projectIds = []
        this.projects = []
      }
    },

    isItemValid(item) {
      const projectMatch = this.projectIds.some(
        (p) => p.projectId === item.projectId
      )
      const hasMedia = item.videos && item.images && item.model

      return projectMatch && hasMedia
    },

    formatDateToDB(date) {
      const [year, month, day] = date.split("-")

      return `${day}-${month}-${year}`
    },

    // This function builds the query string for the 360 URL
    buildQuery(item, isAdmin) {
      const project = this.projects.find((p) => p.projectId === item.projectId)
      if (!project) return ""

      const formattedDate = this.formatDateToDB(item.date)
      const dateObj = project.dates?.find((d) => d.date === formattedDate)
      if (!dateObj) return ""

      const floorObj = dateObj.floors?.find((f) => f.floorID === item.floor)
      if (!floorObj) return ""

      if (!isAdmin) {
        return `${this.$config.public.dashURL}/projects/${
          item.projectId
        }/360?date_id=${dateObj.dateID}&floor=${encodeURIComponent(
          floorObj.floorID
        )}`
      } else {
        return `${this.$config.public.viewer360Url}?project_id=${
          item.projectId
        }&date_id=${dateObj.dateID}&floor=${encodeURIComponent(
          floorObj.floorID
        )}&user=admin`
      }
    },

    async openDeleteDialog(deletedItem) {
      if (
        await this.$confirmDialog.open({
          title: "Delete 360 Asset upload",
          message:
            "Are you sure you want to delete this 360 Asset upload ?. If you are not from the 3D team please contact them before doing this action",
        })
      ) {
        this.delete360Upload(deletedItem.id)
      }
    },
    async delete360Upload(id) {
      try {
        await IngestApi.assets360.deleteUpload(id)
        this.$notifications.success("Successfully deleted entry")
      } catch (e) {
        this.$notifications.error({ text: "Couldn't delete entry", error: e })
      } finally {
        this.refreshTable()
      }
    },
    async generate360UploadLink(payload) {
      try {
        const date = new Date(this.currentSelectedDate)
        const response = await IngestApi.assets360.createUpload(
          payload.project.exid,
          {
            date: `${date.getDate()}-${
              date.getMonth() + 1
            }-${date.getFullYear()}`,
            floor: this.currentSelectedFloor.replace(/[.$/[\]#]/g, " "), // Replace . $ / [ ] # with space
            uploadedBy: this.accountStore.email,
          }
        )
        this.uploadObject = {
          ...response,
          uploadType: _360AssetType.Image,
        }
        this.$setTimeout(() => {
          this.refreshTable()
          this.on360UploadDialogClosed()
        }, 10)
      } catch (e) {
        this.$notifications.error({ text: "Couldn't get token : ", error: e })

        return ""
      }
    },
    copyFloor(floor) {
      navigator.clipboard.writeText(floor)

      this.$notifications.success("Floor copied to clipboard")
    },
    openUploadDialog(item, uploadType) {
      this.uploadObject = {
        ...item,
        uploadType,
      }
      this.assets360Dialog = true
    },
    on360UploadDialogClosed() {
      this.uploadObject = undefined
      this.assets360Dialog = false
      this.currentSelectedDate = ""
      this.currentSelectedFloor = ""
    },
    async refreshTable() {
      this.$root.$emit("refresh-report-table")
      this.queue360Size = "..."
      this.totalQueueSize = "..."
      this.queue360Size = await this.ingestAPI.assets360.get360QueueSize()
      if (this.queue360Size) {
        this.queue360Size = this.queue360Size.size
      }
      this.totalQueueSize = await this.ingestAPI.getTotalQueueSize()
      if (this.totalQueueSize) {
        this.totalQueueSize = this.totalQueueSize.size
      }
    },
  },
}
</script>
